import React, { useRef, useState } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { SHOP_URL } from "../data/plans";

export const PricingCard3D = ({ plan, index = 0 }) => {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const rotateX = useTransform(mouseY, [0, 1], [8, -8]);
  const rotateY = useTransform(mouseX, [0, 1], [-10, 10]);

  const rotateXSpring = useSpring(rotateX, { stiffness: 220, damping: 22 });
  const rotateYSpring = useSpring(rotateY, { stiffness: 220, damping: 22 });

  const handleMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0.5);
    mouseY.set(0.5);
    setHovered(false);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: 1200,
      }}
      data-testid="pricing-card"
      className="group relative"
    >
      <motion.div
        style={{
          rotateX: rotateXSpring,
          rotateY: rotateYSpring,
          transformStyle: "preserve-3d",
        }}
        className="relative h-full rounded-2xl p-6 sm:p-7 flex flex-col"
      >
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: plan.featured
              ? "linear-gradient(180deg, color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface)), var(--osx-surface))"
              : "var(--osx-surface)",
            border: `1px solid ${plan.featured ? "var(--osx-border-strong)" : "var(--osx-border)"}`,
            boxShadow: hovered
              ? plan.featured
                ? "0 30px 80px rgba(2,132,199,0.28), 0 0 0 1px var(--osx-border-strong)"
                : "0 20px 60px rgba(2,132,199,0.16), 0 0 0 1px var(--osx-border-strong)"
              : "var(--osx-shadow-1)",
            transition: "box-shadow 0.3s ease, border-color 0.3s ease",
          }}
        />

        {/* Gradient top accent */}
        <div
          className="absolute top-0 left-6 right-6 h-[2px] rounded-full"
          style={{
            background: plan.featured
              ? "linear-gradient(90deg, transparent, var(--osx-accent-cyan), var(--osx-brand-600), transparent)"
              : "linear-gradient(90deg, transparent, var(--osx-brand-400), transparent)",
            opacity: hovered ? 1 : 0.6,
            transition: "opacity 0.3s ease",
          }}
          aria-hidden
        />

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col" style={{ transform: "translateZ(30px)" }}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <span
              className="inline-flex text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded-md"
              style={{ background: "var(--osx-surface-2)", color: "var(--osx-text-muted)", border: "1px solid var(--osx-border)" }}
            >
              {plan.category}
            </span>
            {plan.badge && (
              <span
                className="inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md text-white"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 6px 18px rgba(2,132,199,0.35)",
                }}
                data-testid="pricing-card-badge"
              >
                {plan.badge}
              </span>
            )}
          </div>

          <h3
            className="text-lg sm:text-xl font-bold mt-2 tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="pricing-card-plan-name"
          >
            {plan.name}
          </h3>

          <div className="mt-4 flex items-baseline gap-1" data-testid="pricing-card-price">
            <span className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
              {plan.price}
            </span>
            <span className="text-sm font-medium" style={{ color: "var(--osx-text-muted)" }}>
              {plan.period}
            </span>
          </div>

          {plan.ram && plan.ram !== "—" && (
            <div
              className="mt-4 inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-lg"
              style={{
                background: "var(--osx-surface-2)",
                border: "1px solid var(--osx-border)",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--osx-accent-cyan)" }} />
              <span className="text-xs font-mono font-semibold" style={{ color: "var(--osx-text)" }}>
                {plan.ram} RAM
              </span>
            </div>
          )}

          <ul className="mt-5 space-y-2.5 flex-1" data-testid="pricing-card-features">
            {plan.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--osx-text-muted)" }}>
                <span
                  className="mt-0.5 inline-flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: "color-mix(in oklab, var(--osx-brand-600) 18%, var(--osx-surface-2))",
                    color: "var(--osx-brand-600)",
                    width: 18, height: 18,
                  }}
                >
                  <Check size={11} strokeWidth={3} />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <a
            href={SHOP_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="pricing-card-contratar-button"
            className="mt-7 group/cta inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold"
            style={{
              background: plan.featured
                ? "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))"
                : "var(--osx-surface-2)",
              color: plan.featured ? "#ffffff" : "var(--osx-text)",
              border: plan.featured ? "none" : "1px solid var(--osx-border-strong)",
              boxShadow: plan.featured ? "0 12px 28px rgba(2,132,199,0.32)" : "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Contratar
            <ArrowRight size={14} className="transition-transform group-hover/cta:translate-x-1" />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};
